<script setup lang="ts">
import type { PropType } from "vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Loader from "../Loader.vue";

const props = defineProps({
  name: {
    type: String,
  },
  default: {
    type: Boolean,
    default: true,
  },
  variant: {
    type: String as PropType<"blue" | "red" | "grey">,
    default: undefined,
  },
  active: {
    type: Boolean,
    default: false,
  },
  loader: {
    type: Boolean,
    default: false,
  },
  spin: {
    type: Boolean,
    default: false,
  },

  width: {
    type: Number,
    default: 16,
  },

  height: {
    type: Number,
    default: 16,
  },

  radius: {
    type: Number,
    default: 8,
  },

  disabled: {
    type: Boolean,
    default: false,
  },
});

const buttonClass = computed(() => {
  if (props.variant) return `svg_button__button_${props.variant}`;
  return props.default ? "svg_button__button_blue" : "svg_button__button_red";
});
</script>

<template>
  <button
    v-if="name"
    :disabled="loader || disabled"
    :class="buttonClass"
    :style="{ borderRadius: radius + 'px' }"
    v-bind="$attrs"
  >
    <SvgIcon
      v-if="!loader"
      :style="{ width: width + 'px', height: height + 'px' }"
      :class="{ 'animate-spin': spin }"
      :name="name"
    />

    <Loader v-else class="h-[16px] w-[16px]" :width="20" :height="20"></Loader>
  </button>
</template>

<style scoped>
.svg_button__button_blue {
  @apply flex items-center justify-center rounded-[8px] h-[32px] w-[32px];
  box-sizing: border-box;
  border: 1px solid var(--primary-blue-color);
  color: var(--primary-blue-color);

  &:hover {
    background-color: var(--grey-light);
    border-color: var(--blue-dark);
    color: var(--blue-dark);
  }

  &:active {
    background-color: var(--grey-lightest);
  }

  &:disabled {
    border: 1px solid var(--grey-medium-light);
    color: var(--grey-medium-light);
  }
}

.svg_button__button_red {
  @apply flex items-center justify-center rounded-[8px] h-[32px] w-[32px];
  box-sizing: border-box;
  border: 1px solid var(--red-dark);
  color: var(--red-dark);

  &:hover {
    background-color: var(--grey-light);
    border-color: var(--red-main);
    color: var(--red-main);
  }

  &:active {
    background-color: var(--grey-lightest);
  }

  &:disabled {
    border: 1px solid var(--grey-medium-light);
    color: var(--grey-medium-light);
  }
}

.svg_button__button_grey {
  @apply flex items-center justify-center rounded-[6px] h-[32px] w-[32px];
  box-sizing: border-box;
  border: 1px solid var(--grey-medium);
  color: var(--grey-medium);

  &:hover {
    border-color: var(--secondary-color);
    color: var(--secondary-color);
  }

  &:active {
    background-color: var(--grey-lightest);
  }

  &:disabled {
    border: 1px solid var(--grey-medium-light);
    color: var(--grey-medium-light);
  }
}
</style>
